import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Draw-on-scroll wrapper for SVG paths
const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i = 0) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 2, ease: EASE, delay: i * 0.15 },
      opacity: { duration: 0.4, delay: i * 0.15 },
    },
  }),
};

/* Golden-hour arc motif — concentric setting-sun arcs */
export const ArcMotif = ({ className = "", stroke = "currentColor" }) => (
  <motion.svg
    viewBox="0 0 400 220"
    fill="none"
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.4 }}
  >
    {[200, 160, 120, 80, 40].map((r, i) => (
      <motion.path
        key={r}
        d={`M ${200 - r} 200 A ${r} ${r} 0 0 1 ${200 + r} 200`}
        stroke={stroke}
        strokeWidth="1"
        variants={draw}
        custom={i}
      />
    ))}
    <motion.circle
      cx="200"
      cy="200"
      r="6"
      fill={stroke}
      initial={{ scale: 0, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
    />
  </motion.svg>
);

/* Architectural blueprint / floor-plan line drawing */
export const Blueprint = ({ className = "", stroke = "currentColor" }) => (
  <motion.svg
    viewBox="0 0 320 320"
    fill="none"
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.3 }}
  >
    {[
      "M40 40 H280 V280 H40 Z",
      "M40 160 H280",
      "M160 40 V280",
      "M160 160 H280",
      "M100 40 V160",
      "M40 220 H160",
      "M220 160 V280",
    ].map((d, i) => (
      <motion.path key={i} d={d} stroke={stroke} strokeWidth="1" variants={draw} custom={i} />
    ))}
    {[
      [100, 100],
      [220, 220],
      [220, 100],
    ].map(([cx, cy], i) => (
      <motion.circle
        key={i}
        cx={cx}
        cy={cy}
        r="4"
        fill={stroke}
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 0.7 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.8 + i * 0.15, ease: EASE }}
      />
    ))}
  </motion.svg>
);

/* Three interlocking circles — Experience · Legacy · Freedom */
export const PillarVenn = ({ className = "", labels = ["Experience", "Legacy", "Freedom"] }) => (
  <motion.svg
    viewBox="0 0 360 300"
    fill="none"
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.4 }}
  >
    <motion.circle cx="140" cy="120" r="90" stroke="currentColor" strokeWidth="1" variants={draw} custom={0} />
    <motion.circle cx="220" cy="120" r="90" stroke="currentColor" strokeWidth="1" variants={draw} custom={1} />
    <motion.circle cx="180" cy="185" r="90" stroke="currentColor" strokeWidth="1" variants={draw} custom={2} />
    <motion.circle
      cx="180"
      cy="145"
      r="5"
      fill="currentColor"
      initial={{ scale: 0 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 1.4, ease: EASE }}
    />
    {[
      { x: 96, y: 92, t: labels[0] },
      { x: 264, y: 92, t: labels[1] },
      { x: 180, y: 262, t: labels[2] },
    ].map((l, i) => (
      <motion.text
        key={l.t}
        x={l.x}
        y={l.y}
        textAnchor="middle"
        className="font-serif"
        fontSize="18"
        fontStyle="italic"
        fill="currentColor"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 1 + i * 0.15 }}
      >
        {l.t}
      </motion.text>
    ))}
  </motion.svg>
);

/* Animated count-up number */
export const CountUp = ({ to, suffix = "", prefix = "", decimals = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(prefix + "0" + suffix);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, { duration: 2, ease: EASE });
    const unsub = mv.on("change", (v) => {
      setDisplay(prefix + v.toFixed(decimals) + suffix);
    });
    return () => {
      controls.stop();
      unsub();
    };
  }, [inView, to, mv, prefix, suffix, decimals]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

/* Thin corner bracket accent */
export const CornerBracket = ({ className = "" }) => (
  <svg viewBox="0 0 40 40" fill="none" className={className}>
    <path d="M1 12 V1 H12" stroke="currentColor" strokeWidth="1" />
    <path d="M39 28 V39 H28" stroke="currentColor" strokeWidth="1" />
  </svg>
);


/* Topographic contour lines — organic site-topography motif */
export const Contours = ({ className = "" }) => (
  <motion.svg
    viewBox="0 0 400 400"
    fill="none"
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
  >
    {[
      "M40 340 C120 300 120 220 200 200 C280 180 300 120 360 120",
      "M40 300 C130 260 140 190 210 168 C285 145 300 90 360 88",
      "M40 260 C140 224 160 158 220 138 C290 116 300 60 360 58",
      "M40 220 C150 190 176 128 232 110 C296 90 300 34 360 30",
      "M40 180 C160 156 192 100 244 84 C300 66 300 12 360 8",
    ].map((d, i) => (
      <motion.path key={i} d={d} stroke="currentColor" strokeWidth="1" variants={draw} custom={i} />
    ))}
  </motion.svg>
);


/* Distinct line-art glyph per movement (0,1,2). Animates on hover via `hovered`. */
export const MovementGlyph = ({ index = 0, hovered = false, className = "" }) => {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.25,
    strokeLinecap: "round",
  };
  return (
    <motion.svg viewBox="0 0 64 64" className={className} {...common}>
      {index === 0 && (
        // Experience Multiplier — expanding ripples
        <>
          {[8, 16, 24].map((r, i) => (
            <motion.circle
              key={r}
              cx="32"
              cy="32"
              r={r}
              animate={hovered ? { scale: [1, 1.12, 1], opacity: [1, 0.5, 1] } : { scale: 1, opacity: 1 }}
              transition={{ duration: 1.4, ease: EASE, delay: i * 0.12, repeat: hovered ? Infinity : 0 }}
              style={{ transformOrigin: "32px 32px" }}
            />
          ))}
          <circle cx="32" cy="32" r="2.5" fill="currentColor" stroke="none" />
        </>
      )}
      {index === 1 && (
        // Legacy Layer — stacked strata threaded together
        <>
          <path d="M14 22 H50" />
          <path d="M14 32 H50" />
          <path d="M14 42 H50" />
          <motion.path
            d="M32 14 V50"
            animate={hovered ? { pathLength: [0, 1] } : { pathLength: 1 }}
            transition={{ duration: 1.2, ease: EASE, repeat: hovered ? Infinity : 0 }}
          />
          <circle cx="32" cy="22" r="2.2" fill="currentColor" stroke="none" />
          <circle cx="32" cy="42" r="2.2" fill="currentColor" stroke="none" />
        </>
      )}
      {index === 2 && (
        // Owner Freedom — open orbit with travelling point
        <>
          <path d="M32 10 A22 22 0 1 1 14 20" />
          <motion.g
            animate={hovered ? { rotate: 360 } : { rotate: 0 }}
            transition={{ duration: 6, ease: "linear", repeat: hovered ? Infinity : 0 }}
            style={{ transformOrigin: "32px 32px" }}
          >
            <circle cx="32" cy="10" r="3" fill="currentColor" stroke="none" />
          </motion.g>
          <circle cx="32" cy="32" r="2.5" fill="currentColor" stroke="none" />
        </>
      )}
    </motion.svg>
  );
};
